/**
 * Guide step 4: Contact and screening (BR-LSI6-01, BR-102). Contact name and phone are required to
 * publish but never block moving on; the screening criteria are optional and advisory. The AI leasing
 * agent is its own step. One "Save and continue" saves whichever editor is dirty.
 */

import { useCallback, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Badge } from '../../../../components/ui/badge';
import { Button } from '../../../../components/ui/button';
import { Skeleton } from '../../../../components/ui/skeleton';
import { fetchListing, type Listing } from '../../../../lib/leasing.api';
import { ContactInfoEditor } from '../../listing-detail/ContactInfoEditor';
import { ScreeningCriteriaEditor } from '../../listing-detail/ScreeningCriteriaEditor';
import type { GuideStepHandle, GuideStepProps } from '../guide-steps';

function ContactScreeningBody({
  listing,
  handleRef,
  onDirtyChange,
  onSavingChange,
}: GuideStepProps & { listing: Listing }) {
  const queryClient = useQueryClient();
  const contactRef = useRef<GuideStepHandle>(null);
  const screeningRef = useRef<GuideStepHandle>(null);
  const [contactDirty, setContactDirty] = useState(false);
  const [screeningDirty, setScreeningDirty] = useState(false);
  const [contactSaving, setContactSaving] = useState(false);
  const [screeningSaving, setScreeningSaving] = useState(false);

  const detail = useQuery({ queryKey: ['listing', listing.id], queryFn: () => fetchListing(listing.id) });

  useEffect(() => onDirtyChange(contactDirty || screeningDirty), [contactDirty, screeningDirty, onDirtyChange]);
  useEffect(() => onSavingChange(contactSaving || screeningSaving), [contactSaving, screeningSaving, onSavingChange]);

  // Both editors save independently; the step reports failure if either failed (each shows its own error).
  useImperativeHandle(handleRef, () => ({
    save: async () => {
      const results = await Promise.all([contactRef.current?.save() ?? true, screeningRef.current?.save() ?? true]);
      return results.every(Boolean);
    },
    isDirty: () => (contactRef.current?.isDirty() ?? false) || (screeningRef.current?.isDirty() ?? false),
  }));

  const refreshListing = useCallback(() => {
    void queryClient.invalidateQueries({ queryKey: ['listing', listing.id] });
    void queryClient.invalidateQueries({ queryKey: ['listings'] });
  }, [queryClient, listing.id]);

  return (
    <div data-testid="guide-step-contact" className="flex flex-col gap-6">
      <header>
        <h1 className="text-2xl font-semibold text-foreground" data-testid="guide-step-heading">
          Contact and screening
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">Who prospects reach, and what you look for in an applicant.</p>
      </header>

      {detail.isPending ? (
        <div className="flex flex-col gap-4" data-testid="guide-step-contact-loading" aria-busy="true">
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-72 w-full" />
        </div>
      ) : detail.isError ? (
        <div
          role="alert"
          data-testid="guide-step-contact-error"
          className="rounded-md border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive"
        >
          <p>Could not load the contact and screening details for this listing.</p>
          <Button
            type="button"
            variant="outline"
            size="sm"
            className="mt-2 min-h-[44px]"
            data-testid="guide-step-contact-retry"
            onClick={() => void detail.refetch()}
          >
            Retry
          </Button>
        </div>
      ) : (
        <>
          <section
            aria-labelledby="guide-contact-title"
            data-testid="guide-contact-card"
            className="rounded-lg border border-border bg-card p-5"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 id="guide-contact-title" className="text-base font-semibold text-foreground">
                  Contact
                </h2>
                <p className="text-sm text-muted-foreground">Shown on the listing. Prospects can call this number.</p>
              </div>
              <Badge variant="warning" className="shrink-0" data-testid="guide-contact-required-badge">
                Required to publish
              </Badge>
            </div>
            <ContactInfoEditor
              variant="bare"
              embedded
              listing={listing}
              intakeAddress={detail.data.intakeAddress}
              contactPhone={detail.data.contactPhone}
              contactName={detail.data.contactName}
              activationError={null}
              onSaved={refreshListing}
              handleRef={contactRef}
              onDirtyChange={setContactDirty}
              onSavingChange={setContactSaving}
            />
          </section>

          <section
            aria-labelledby="guide-screening-title"
            data-testid="guide-screening-card"
            className="rounded-lg border border-border bg-card p-5"
          >
            <div className="mb-4 flex items-start justify-between gap-3">
              <div className="min-w-0">
                <h2 id="guide-screening-title" className="text-base font-semibold text-foreground">
                  Screening criteria
                </h2>
                <p className="text-sm text-muted-foreground">
                  Optional and advisory. Nothing is rejected automatically. You see them next to each applicant&apos;s
                  answers.
                </p>
              </div>
              <Badge variant="secondary" className="shrink-0" data-testid="guide-screening-optional-badge">
                Optional
              </Badge>
            </div>
            <ScreeningCriteriaEditor
              variant="bare"
              embedded
              listing={listing}
              screeningCriteria={detail.data.screeningCriteria}
              onUpdated={refreshListing}
              handleRef={screeningRef}
              onDirtyChange={setScreeningDirty}
              onSavingChange={setScreeningSaving}
            />
          </section>
        </>
      )}
    </div>
  );
}

export function ContactScreeningStep(props: GuideStepProps) {
  // Only reachable once Listing details has been saved (the guide redirects earlier visits).
  if (!props.listing) return null;
  return <ContactScreeningBody {...props} listing={props.listing} />;
}
